import React, { useState, useRef, useEffect, useMemo } from 'react';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';
import { ChevronDownIcon } from './Icons';
import TextInput from './TextInput';
import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';

interface AutocompleteProps {
  endpoint?: string;
  options?: Array<string | { value: string; label: string }>;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  maxLength?: number;
}

const Autocomplete: React.FC<AutocompleteProps> = ({
  endpoint,
  options: staticOptions = [],
  value = '',
  onChange,
  placeholder = 'Type to search...',
  className,
  disabled = false,
  required = false,
  error,
  maxLength,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { colors } = useTheme();

  // Build URL with query params for the API call
  const apiUrl = useMemo(() => {
    if (!endpoint) return null;
    if (searchTerm && searchTerm.trim()) {
      const params = new URLSearchParams({ search: searchTerm.trim() });
      return `${endpoint}?${params.toString()}`;
    }
    return endpoint;
  }, [endpoint, searchTerm]);

  // Fetch options from API if endpoint is provided
  const { data: apiOptions = [], isLoading, error: apiError } = useSWR<any[]>(apiUrl, fetcher);

  // Use API options directly (API handles filtering) or static options
  const filteredOptions = useMemo(() => {
    if (endpoint) {
      // API returns filtered results, use them directly
      const options = Array.isArray(apiOptions) ? apiOptions.filter(Boolean) : [];
      return options;
    } else {
      // For static options, do client-side filtering
      if (!inputValue) return staticOptions.slice(0, 20);

      const lowerInput = inputValue.toLowerCase();
      return staticOptions
        .filter((opt) => {
          const optionText = typeof opt === 'string' ? opt : opt.label || String(opt);
          return optionText.toLowerCase().includes(lowerInput);
        })
        .slice(0, 20);
    }
  }, [inputValue, endpoint, apiOptions, staticOptions]);

  // Sync input to value: when we have options, show the option's label (for edit); otherwise show value (e.g. id or free text)
  useEffect(() => {
    const val = value?.toString().trim() ?? '';
    const opts = endpoint ? filteredOptions : staticOptions;
    if (val && opts && opts.length > 0) {
      const option = opts.find((o: string | { value: string; label: string }) => {
        if (typeof o === 'string') return o === val;
        const oVal = o.value != null ? String(o.value) : '';
        return oVal === val;
      });
      if (option) {
        const label = typeof option === 'string' ? option : (option.label ?? String(option.value ?? ''));
        setInputValue(label);
        return;
      }
    }
    setInputValue(val);
  }, [value, endpoint, filteredOptions, staticOptions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (event: { target: { name?: string; value: string; type: string } }) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    setSearchTerm(newValue);
    setIsOpen(true);

    // Call onChange immediately for free text input
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleSelectOption = (option: string | { value: string; label: string }) => {
    const optionText = typeof option === 'string' ? option : option.label || String(option);
    setInputValue(optionText);
    setSearchTerm('');
    setIsOpen(false);
    if (onChange) {
      onChange(optionText);
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'ArrowDown' && filteredOptions.length > 0) {
      e.preventDefault();
      // Focus first option (could be enhanced to navigate through options)
    }
  };

  return (
    <div ref={dropdownRef} className={cn('relative', className)}>
      <div className="relative">
        <TextInput
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          error={error}
          maxLength={maxLength}
          className="pr-8"
        />
        <ChevronDownIcon
          className={cn(
            'absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none transition-transform',
            colors.textMuted,
            isOpen && 'rotate-180'
          )}
        />
      </div>

      {isOpen && (
        <div
          className={cn(
            'absolute z-50 w-full mt-1 border rounded-md shadow-lg max-h-60 overflow-y-auto',
            colors.surface,
            colors.border
          )}
        >
          {isLoading && (
            <div className={`px-3 py-2 text-sm ${colors.textMuted}`}>Loading...</div>
          )}
          {!isLoading && apiError && (
            <div className={cn('px-3 py-2 text-sm', colors.error)}>Error loading options</div>
          )}
          {!isLoading && !apiError && filteredOptions.length === 0 && (
            <div className={`px-3 py-2 text-sm ${colors.textMuted}`}>No options found</div>
          )}
          {!isLoading &&
            filteredOptions.length > 0 &&
            filteredOptions.map((option, index) => {
              const optionText = typeof option === 'string' ? option : option.label || String(option);
              const isSelected = inputValue === optionText;

              return (
                <div
                  key={index}
                  onClick={() => handleSelectOption(option)}
                  className={cn(
                    'px-3 py-2 cursor-pointer transition-colors',
                    isSelected ? cn(colors.surfaceHover, colors.primaryFg) : colors.interactiveHover,
                    colors.text
                  )}
                >
                  {optionText}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default Autocomplete;
